import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import {
  createCanvasSnapshotQueryFilter,
  isCanvasSnapshotCacheData,
} from '@/hooks/api/useCanvasSnapshot';
import { shouldAdoptDraftByUpdatedAt } from '@/lib/generation/draft-revision-guard';
import {
  buildGenerationTargetCacheKey,
  buildGenerationResourceKey,
  normalizeGenerationReferences,
} from '@/lib/generation/generation-reference';
import { buildGenerationReferenceIdentity } from '@/lib/generation/generation-reference-draft-reconcile';
import {
  buildGenerationReferenceDraftOperations,
  buildRebasedGenerationReferenceDraftOperations,
  canAdvanceComposerDesiredOnNoop,
} from '@/lib/generation/generation-reference-draft-operations';
import { shouldAdoptDraftForTarget } from '@/lib/generation/generation-reference-draft-revision';
import {
  buildGenerationReferenceIdentitySignature,
  classifyGenerationReferenceSyncError,
} from '@/lib/generation/generation-reference-submit-guard';
import { enrichSnapshotReferenceSources } from '@/lib/generation/reference-source-hints';
import { generationReferenceDraftsApi } from '@/services/api/generation-reference-drafts';
import type { CanvasSnapshot } from '@/types/canvas-snapshot';
import type {
  GenerationReferenceDraft,
  GenerationReferenceDraftOperation,
  GenerationReferenceItem,
  GenerationTargetKey,
} from '@/types/generation-reference';

export { shouldAdoptDraftForTarget };
export {
  buildGenerationReferenceDraftOperations,
  buildRebasedGenerationReferenceDraftOperations,
  canAdvanceComposerDesiredOnNoop,
};

export const generationReferenceDraftKeys = {
  all: ['generation-reference-drafts'] as const,
  target: (target: GenerationTargetKey) =>
    [...generationReferenceDraftKeys.all, buildGenerationTargetCacheKey(target)] as const,
};

export function useGenerationReferenceDraft(
  target: GenerationTargetKey | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: target
      ? generationReferenceDraftKeys.target(target)
      : generationReferenceDraftKeys.all,
    queryFn: () => generationReferenceDraftsApi.get(target!),
    enabled: Boolean(target) && (options?.enabled ?? true),
  });
}

export function patchSnapshotDraft(
  snapshot: CanvasSnapshot | undefined,
  draft: GenerationReferenceDraft
): CanvasSnapshot | undefined {
  // history 查询与 snapshot 共用前缀时，可能误传入非 snapshot 数据。
  if (!isCanvasSnapshotCacheData(snapshot)) return snapshot;
  const targetKey = buildGenerationTargetCacheKey(draft.target);
  const nextDrafts = snapshot.drafts.filter(
    (item) => buildGenerationTargetCacheKey(item.target) !== targetKey
  );
  if (draft.references.length > 0) {
    nextDrafts.push(draft);
  }
  const withDrafts = { ...snapshot, drafts: nextDrafts };
  // 乐观补 sources：未展开历史时 collapsed 依赖 sourcePlanId/sourceTagId。
  return enrichSnapshotReferenceSources(withDrafts, draft);
}

/**
 * 将草稿同时写入 draft query 与 canvas-snapshot.drafts。
 * 两套缓存各自按 updatedAt 单调 adopt：draft query 已较新时仍可补齐落后的 snapshot（避免 @ 同步但草稿边不刷新）。
 */
export function applyGenerationReferenceDraftChanged(
  queryClient: QueryClient,
  draft: GenerationReferenceDraft
): void {
  const queryKey = generationReferenceDraftKeys.target(draft.target);
  const cached = queryClient.getQueryData<GenerationReferenceDraft>(queryKey);
  const adoptQuery = shouldAdoptDraftByUpdatedAt(cached?.updatedAt, draft.updatedAt);
  if (adoptQuery) {
    queryClient.setQueryData(queryKey, draft);
  }

  const targetKey = buildGenerationTargetCacheKey(draft.target);
  queryClient.setQueriesData<CanvasSnapshot>(createCanvasSnapshotQueryFilter(), (snapshot) => {
    if (!snapshot || snapshot.projectId !== draft.target.projectId) return snapshot;
    if (!isCanvasSnapshotCacheData(snapshot)) return snapshot;
    const existing = snapshot.drafts.find(
      (item) => buildGenerationTargetCacheKey(item.target) === targetKey
    );
    if (!shouldAdoptDraftByUpdatedAt(existing?.updatedAt, draft.updatedAt)) {
      return snapshot;
    }
    return patchSnapshotDraft(snapshot, draft);
  });
}

export async function applyGenerationReferenceDraftOperations(params: {
  target: GenerationTargetKey;
  operations: GenerationReferenceDraftOperation[];
}): Promise<GenerationReferenceDraft | null> {
  let latest: GenerationReferenceDraft | null = null;
  for (const operation of params.operations) {
    if (operation.operation === 'add') {
      latest = await generationReferenceDraftsApi.addEdge({
        target: params.target,
        reference: operation.reference,
      });
    } else {
      latest = await generationReferenceDraftsApi.removeEdge({
        target: params.target,
        resourceType: operation.resourceType,
        resourceId: operation.resourceId,
        role: operation.role,
      });
    }
  }
  return latest;
}

export function usePatchGenerationReferenceDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      target: GenerationTargetKey;
      operations: GenerationReferenceDraftOperation[];
    }) => applyGenerationReferenceDraftOperations(params),
    onSuccess: (draft) => {
      if (draft) applyGenerationReferenceDraftChanged(queryClient, draft);
    },
  });
}

/**
 * 组件层访问引用草稿缓存 / 拉取 / 对齐写回的统一入口，
 * 避免 UI 直接依赖 queryClient 或 API service。
 */
export function useGenerationReferenceDraftCacheHelpers() {
  const queryClient = useQueryClient();

  const getCachedDraft = useCallback(
    (target: GenerationTargetKey): GenerationReferenceDraft | null => {
      return (
        queryClient.getQueryData<GenerationReferenceDraft>(
          generationReferenceDraftKeys.target(target)
        ) ?? null
      );
    },
    [queryClient]
  );

  const fetchDraft = useCallback(
    (target: GenerationTargetKey): Promise<GenerationReferenceDraft> => {
      return queryClient.fetchQuery({
        queryKey: generationReferenceDraftKeys.target(target),
        queryFn: () => generationReferenceDraftsApi.get(target),
        staleTime: 0,
      });
    },
    [queryClient]
  );

  const applyDraftChanged = useCallback(
    (draft: GenerationReferenceDraft): void => {
      applyGenerationReferenceDraftChanged(queryClient, draft);
    },
    [queryClient]
  );

  /** /message consume 后强制 GET 并写回缓存/画布投影（不 PATCH）。 */
  const refetchDraftAfterConsume = useCallback(
    async (target: GenerationTargetKey): Promise<GenerationReferenceDraft> => {
      const draft = await fetchDraft(target);
      applyDraftChanged(draft);
      return draft;
    },
    [applyDraftChanged, fetchDraft]
  );

  return { getCachedDraft, fetchDraft, applyDraftChanged, refetchDraftAfterConsume };
}

export type SyncGenerationReferencesOptions = {
  /**
   * 显式要求删除的 identity（即使不在 lastComposerDesired）。
   * 用于面板取消关联 / 删 @chip / 剪刀断线等「用户明确断开」路径。
   */
  forceRemoveIdentities?: ReadonlyArray<string>;
  /**
   * 仅按 forceRemoveIdentities 从「执行时的 server 快照」剔除，不把调用时的 references
   * 快照当作最终 desired。用于切到帧模式清普通参考：避免排队时 desired=[]，
   * 在应用提示词写入首尾帧之后执行，把新帧整表删光。
   */
  forceRemoveOnly?: boolean;
  /** 内部同步来源；仅 auto-merge 会进入提交时的精确重试槽。 */
  syncReason?:
    | 'auto-merge'
    | 'auto-retry'
    | 'manual'
    | 'submit-convergence'
    | 'availability-cleanup';
  /** 自动 merge 的输入框引用 identity 签名，不包含仅已连线的引用。 */
  composerIntentSignature?: string | null;
};

export type GenerationReferenceDraftAutoSyncFailure = {
  failureId: number;
  kind: 'network' | 'terminal';
  targetKey: string;
  composerIntentSignature: string;
  intentId: number;
  message: string;
};

export type RetryGenerationReferenceAutoSyncResult =
  | { status: 'none' | 'success' | 'superseded' | 'target-changed' }
  | { status: 'unavailable'; message: string; unavailableResourceKeys: string[] }
  | { status: 'network' | 'terminal'; message: string };

type GenerationReferenceDraftManualSyncFailure = {
  failureId: number;
  error: unknown;
  requiredPresentIdentities: string[];
  requiredAbsentIdentities: string[];
};

function buildManualSyncFailureRequirements(params: {
  operations: GenerationReferenceDraftOperation[];
  intentReferences: GenerationReferenceItem[];
  forceRemoveIdentities?: ReadonlyArray<string>;
  forceRemoveOnly: boolean;
}): Pick<
  GenerationReferenceDraftManualSyncFailure,
  'requiredPresentIdentities' | 'requiredAbsentIdentities'
> {
  const requiredPresentIdentities = new Set<string>();
  const requiredAbsentIdentities = new Set<string>(params.forceRemoveIdentities ?? []);

  if (params.operations.length > 0) {
    for (const operation of params.operations) {
      const identity = buildGenerationReferenceIdentity(
        operation.operation === 'add' ? operation.reference : operation
      );
      if (operation.operation === 'add') {
        requiredPresentIdentities.add(identity);
        requiredAbsentIdentities.delete(identity);
      } else {
        requiredAbsentIdentities.add(identity);
        requiredPresentIdentities.delete(identity);
      }
    }
  } else if (!params.forceRemoveOnly) {
    for (const reference of params.intentReferences) {
      requiredPresentIdentities.add(buildGenerationReferenceIdentity(reference));
    }
  }

  return {
    requiredPresentIdentities: [...requiredPresentIdentities],
    requiredAbsentIdentities: [...requiredAbsentIdentities],
  };
}

function isManualSyncFailureSatisfied(
  failure: GenerationReferenceDraftManualSyncFailure,
  references: GenerationReferenceItem[]
): boolean {
  const currentIdentities = new Set(references.map(buildGenerationReferenceIdentity));
  return (
    failure.requiredPresentIdentities.every((identity) => currentIdentities.has(identity)) &&
    failure.requiredAbsentIdentities.every((identity) => !currentIdentities.has(identity))
  );
}

export interface GenerationReferenceDraftSyncState {
  draft: GenerationReferenceDraft | null;
  isLoading: boolean;
  isSaving: boolean;
  syncReferences: (
    references: GenerationReferenceItem[],
    options?: SyncGenerationReferencesOptions
  ) => Promise<void>;
  /** 等待进行中的 syncReferences 链完成。 */
  awaitPendingSync: () => Promise<void>;
  retryAutoSyncFailure: (params: {
    expectedTargetKey?: GenerationTargetKey;
    expectedComposerIntentSignature: string;
  }) => Promise<RetryGenerationReferenceAutoSyncResult>;
  getUnavailableResourceKeys: () => ReadonlySet<string>;
  reconcileUnavailableResourceKeys: (resourceKeys: ReadonlySet<string>) => void;
}

/**
 * 服务端引用草稿同步：以 desired 集合为目标，串行提交幂等 add/remove。
 * 不按 CAS revision 预检；协作冲突以 updatedAt 整包覆盖，不自动清本地 @。
 *
 * 承载对 API service / queryClient 的直接访问，使 UI 组件保持在展示层，
 * 不直接依赖 service 与缓存实现（前端分层约束）。
 */
export function useGenerationReferenceDraftSync(
  target: GenerationTargetKey,
  options?: { enabled?: boolean }
): GenerationReferenceDraftSyncState {
  const queryClient = useQueryClient();
  const draftQuery = useGenerationReferenceDraft(target, {
    enabled: options?.enabled ?? true,
  });
  const { mutateAsync, isPending } = usePatchGenerationReferenceDraft();
  const draftRef = useRef<GenerationReferenceDraft | null>(null);
  const autoSyncFailureByTargetRef = useRef<Map<string, GenerationReferenceDraftAutoSyncFailure>>(
    new Map()
  );
  const autoSyncIntentByTargetRef = useRef<
    Map<
      string,
      {
        intentId: number;
        target: GenerationTargetKey;
        references: GenerationReferenceItem[];
        options?: SyncGenerationReferencesOptions;
        composerIntentSignature: string;
      }
    >
  >(new Map());
  const unavailableResourceKeysByTargetRef = useRef<Map<string, Set<string>>>(new Map());
  const manualSyncFailuresByTargetRef = useRef<
    Map<string, GenerationReferenceDraftManualSyncFailure[]>
  >(new Map());
  /**
   * 本端上次「已对齐」的 desired，用作意图 rebase 的 base；勿用 server 快照当 base。
   * 仅在 add/remove 成功，或 no-op 且 server ⊆ desired 时推进，避免空 base 吞删后毒化。
   */
  const lastComposerDesiredRef = useRef<GenerationReferenceItem[] | null>(null);
  const queueTailRef = useRef<Promise<void>>(Promise.resolve());
  const nextFailureIdRef = useRef(1);
  const nextIntentIdRef = useRef(1);
  const targetKey = buildGenerationTargetCacheKey(target);
  const targetRef = useRef(target);

  const clearSatisfiedManualSyncFailures = useCallback(
    (syncTargetKey: string, references: GenerationReferenceItem[]) => {
      const failures = manualSyncFailuresByTargetRef.current.get(syncTargetKey);
      if (!failures || failures.length === 0) return;
      const remaining = failures.filter(
        (failure) => !isManualSyncFailureSatisfied(failure, references)
      );
      if (remaining.length === 0) {
        manualSyncFailuresByTargetRef.current.delete(syncTargetKey);
      } else {
        manualSyncFailuresByTargetRef.current.set(syncTargetKey, remaining);
      }
    },
    []
  );

  const rememberUnavailableResourceKeys = useCallback(
    (syncTargetKey: string, resourceKeys: ReadonlySet<string>) => {
      if (resourceKeys.size === 0) return;
      const existing = unavailableResourceKeysByTargetRef.current.get(syncTargetKey) ?? new Set();
      for (const resourceKey of resourceKeys) existing.add(resourceKey);
      unavailableResourceKeysByTargetRef.current.set(syncTargetKey, existing);
    },
    []
  );

  const reconcileUnavailableResourceKeys = useCallback(
    (resourceKeys: ReadonlySet<string>) => {
      const existing = unavailableResourceKeysByTargetRef.current.get(targetKey);
      if (!existing || existing.size === 0) return;
      for (const resourceKey of [...existing]) {
        if (resourceKeys.has(resourceKey)) continue;
        existing.delete(resourceKey);
      }
      if (existing.size === 0) {
        unavailableResourceKeysByTargetRef.current.delete(targetKey);
      } else {
        unavailableResourceKeysByTargetRef.current.set(targetKey, existing);
      }
    },
    [targetKey]
  );

  const getUnavailableResourceKeys = useCallback(
    (): ReadonlySet<string> =>
      new Set(unavailableResourceKeysByTargetRef.current.get(targetKey) ?? new Set<string>()),
    [targetKey]
  );

  // target 切换时同步 ref，并立刻丢弃上一节点 draftRef，避免 syncChain 读到旧草稿。
  useEffect(() => {
    targetRef.current = target;
    lastComposerDesiredRef.current = null;
    const data = draftQuery.data;
    if (data && shouldAdoptDraftForTarget(data, target)) {
      draftRef.current = data;
      // GET / refetch 只写 draft query；补齐 snapshot.drafts，避免 @ 有引用但画布草稿边不刷新。
      applyGenerationReferenceDraftChanged(queryClient, data);
      return;
    }
    draftRef.current = null;
  }, [draftQuery.data, queryClient, target, targetKey]);

  const syncReferences = useCallback(
    async (
      references: GenerationReferenceItem[],
      options?: SyncGenerationReferencesOptions
    ): Promise<void> => {
      const syncTarget = targetRef.current;
      const syncTargetKey = buildGenerationTargetCacheKey(syncTarget);
      const forceRemoveIdentities = options?.forceRemoveIdentities;
      const forceRemoveOnly = options?.forceRemoveOnly === true;
      const syncReason = options?.syncReason ?? 'manual';
      const intentReferences = normalizeGenerationReferences(references);
      const composerIntentSignature =
        options?.composerIntentSignature ??
        buildGenerationReferenceIdentitySignature(intentReferences);
      const intentId = syncReason === 'auto-merge' ? nextIntentIdRef.current++ : null;
      let attemptedOperations: GenerationReferenceDraftOperation[] = [];
      if (syncReason === 'auto-merge' && intentId !== null) {
        autoSyncIntentByTargetRef.current.set(syncTargetKey, {
          intentId,
          target: syncTarget,
          references: intentReferences,
          options,
          composerIntentSignature,
        });
      }
      const clearCoveredAutoFailure = () => {
        if (syncReason !== 'auto-merge' || intentId === null) return;
        const latestIntent = autoSyncIntentByTargetRef.current.get(syncTargetKey);
        if (!latestIntent || latestIntent.intentId !== intentId) return;
        const currentFailure = autoSyncFailureByTargetRef.current.get(syncTargetKey);
        if (currentFailure && currentFailure.intentId <= intentId) {
          autoSyncFailureByTargetRef.current.delete(syncTargetKey);
        }
      };
      const execute = async (): Promise<GenerationReferenceDraft | null> => {
        const activeTarget = targetRef.current;
        // 链上可能跨 target；只服务发起时的 syncTarget，完成后也按当前 target 决定是否写 draftRef。
        if (buildGenerationTargetCacheKey(activeTarget) !== syncTargetKey) {
          return draftRef.current;
        }
        const refDraft =
          draftRef.current && shouldAdoptDraftForTarget(draftRef.current, syncTarget)
            ? draftRef.current
            : null;
        let current =
          refDraft ??
          (await queryClient.fetchQuery({
            queryKey: generationReferenceDraftKeys.target(syncTarget),
            queryFn: () => generationReferenceDraftsApi.get(syncTarget),
          }));
        if (!current) {
          throw new Error('引用草稿加载失败');
        }
        const removeSet = new Set(forceRemoveIdentities ?? []);
        // forceRemoveOnly：以执行时 server 为准剔除，避免排队时的空/陈旧 desired 误删后写入的帧。
        const desiredBeforeFilter = forceRemoveOnly
          ? normalizeGenerationReferences(
              current.references.filter(
                (reference) => !removeSet.has(buildGenerationReferenceIdentity(reference))
              )
            )
          : intentReferences;
        const unavailableResourceKeysForTarget =
          unavailableResourceKeysByTargetRef.current.get(syncTargetKey) ?? new Set<string>();
        const desired = desiredBeforeFilter.filter(
          (reference) =>
            !unavailableResourceKeysForTarget.has(buildGenerationResourceKey(reference))
        );
        // 以「上次 composer desired」为 base：不删本端意图里从未出现过的对端新增。
        // forceRemoveIdentities：面板取消关联等显式断开，即使不在 base 也必须 remove。
        const composerBase = lastComposerDesiredRef.current ?? [];
        const operations = buildRebasedGenerationReferenceDraftOperations({
          serverCurrent: current.references,
          baseCurrent: composerBase,
          desired,
          forceRemoveIdentities,
        });
        attemptedOperations = operations;
        if (operations.length === 0) {
          const isStillActiveTarget =
            buildGenerationTargetCacheKey(targetRef.current) === syncTargetKey;
          if (
            isStillActiveTarget &&
            !forceRemoveOnly &&
            canAdvanceComposerDesiredOnNoop({
              serverCurrent: current.references,
              desired,
            })
          ) {
            lastComposerDesiredRef.current = desired;
          }
          if (isStillActiveTarget && shouldAdoptDraftForTarget(current, targetRef.current)) {
            draftRef.current = current;
          }
          clearSatisfiedManualSyncFailures(syncTargetKey, current.references);
          clearCoveredAutoFailure();
          return current;
        }
        const nextDraft = await mutateAsync({
          target: syncTarget,
          operations,
        });
        if (nextDraft) {
          current = nextDraft;
        }
        const isStillActiveTarget =
          buildGenerationTargetCacheKey(targetRef.current) === syncTargetKey;
        if (isStillActiveTarget && !forceRemoveOnly) {
          lastComposerDesiredRef.current = desired;
        }
        if (isStillActiveTarget && shouldAdoptDraftForTarget(current, targetRef.current)) {
          draftRef.current = current;
        }
        clearSatisfiedManualSyncFailures(syncTargetKey, current.references);
        clearCoveredAutoFailure();
        return current;
      };
      const taskPromise = queueTailRef.current.then(execute, execute);
      const classifiedTaskPromise = taskPromise.catch((error) => {
        const classification = classifyGenerationReferenceSyncError(error);
        if (classification.kind === 'unavailable') {
          rememberUnavailableResourceKeys(syncTargetKey, classification.unavailableResourceKeys);
        }
        if (buildGenerationTargetCacheKey(targetRef.current) === syncTargetKey) {
          draftRef.current = null;
        }
        if (
          syncReason === 'auto-merge' &&
          intentId !== null &&
          classification.kind !== 'unavailable'
        ) {
          const latestIntent = autoSyncIntentByTargetRef.current.get(syncTargetKey);
          if (latestIntent?.intentId === intentId) {
            autoSyncFailureByTargetRef.current.set(syncTargetKey, {
              failureId: nextFailureIdRef.current++,
              kind: classification.kind,
              targetKey: syncTargetKey,
              composerIntentSignature,
              intentId,
              message: classification.message,
            });
          }
        }
        if (
          classification.kind !== 'unavailable' &&
          syncReason !== 'auto-merge' &&
          syncReason !== 'auto-retry' &&
          syncReason !== 'availability-cleanup'
        ) {
          const requirements = buildManualSyncFailureRequirements({
            operations: attemptedOperations,
            intentReferences,
            forceRemoveIdentities,
            forceRemoveOnly,
          });
          const failures = manualSyncFailuresByTargetRef.current.get(syncTargetKey) ?? [];
          failures.push({
            failureId: nextFailureIdRef.current++,
            error,
            ...requirements,
          });
          manualSyncFailuresByTargetRef.current.set(syncTargetKey, failures);
        }
        throw error;
      });
      // continuation tail 始终可继续，但必须等失败分类/记账完成后才 settle；
      // 否则 awaitPendingSync 可能先醒来，短暂看不到刚发生的手动同步失败。
      queueTailRef.current = classifiedTaskPromise.then(
        () => undefined,
        () => undefined
      );
      await classifiedTaskPromise;
    },
    [clearSatisfiedManualSyncFailures, mutateAsync, queryClient, rememberUnavailableResourceKeys]
  );

  const awaitPendingSync = useCallback(async (): Promise<void> => {
    await queueTailRef.current;
    const activeTargetKey = buildGenerationTargetCacheKey(targetRef.current);
    const failures = manualSyncFailuresByTargetRef.current.get(activeTargetKey);
    if (failures && failures.length > 0) {
      throw failures[failures.length - 1].error;
    }
  }, []);

  const retryAutoSyncFailure = useCallback(
    async (params: {
      expectedTargetKey?: GenerationTargetKey;
      expectedComposerIntentSignature: string;
    }): Promise<RetryGenerationReferenceAutoSyncResult> => {
      const activeTargetKey = buildGenerationTargetCacheKey(targetRef.current);
      if (
        params.expectedTargetKey &&
        buildGenerationTargetCacheKey(params.expectedTargetKey) !== activeTargetKey
      ) {
        return { status: 'target-changed' };
      }
      const failure = autoSyncFailureByTargetRef.current.get(activeTargetKey) ?? null;
      const intent = autoSyncIntentByTargetRef.current.get(activeTargetKey) ?? null;
      if (!failure || !intent) {
        return { status: 'none' };
      }
      if (params.expectedTargetKey) {
        const expectedTargetKey = buildGenerationTargetCacheKey(params.expectedTargetKey);
        if (
          expectedTargetKey !== failure.targetKey ||
          expectedTargetKey !== buildGenerationTargetCacheKey(intent.target)
        ) {
          return { status: 'superseded' };
        }
      }
      if (
        failure.intentId !== intent.intentId ||
        params.expectedComposerIntentSignature !== intent.composerIntentSignature ||
        params.expectedComposerIntentSignature !== failure.composerIntentSignature
      ) {
        const currentFailure = autoSyncFailureByTargetRef.current.get(activeTargetKey);
        if (currentFailure?.failureId === failure.failureId) {
          autoSyncFailureByTargetRef.current.delete(activeTargetKey);
        }
        return { status: 'superseded' };
      }
      if (failure.kind === 'terminal') {
        return { status: 'terminal', message: failure.message };
      }
      try {
        await syncReferences(intent.references, {
          ...intent.options,
          syncReason: 'auto-retry',
        });
      } catch (error) {
        const classification = classifyGenerationReferenceSyncError(error);
        if (classification.kind === 'unavailable') {
          return {
            status: 'unavailable',
            message: classification.message,
            unavailableResourceKeys: [...classification.unavailableResourceKeys],
          };
        }
        if (classification.kind === 'terminal') {
          const currentFailure = autoSyncFailureByTargetRef.current.get(activeTargetKey);
          if (currentFailure?.failureId === failure.failureId) {
            autoSyncFailureByTargetRef.current.set(activeTargetKey, {
              ...failure,
              kind: 'terminal',
              message: classification.message,
            });
          }
        }
        return { status: classification.kind, message: classification.message };
      }
      const currentFailure = autoSyncFailureByTargetRef.current.get(activeTargetKey);
      if (currentFailure?.failureId === failure.failureId) {
        autoSyncFailureByTargetRef.current.delete(activeTargetKey);
      }
      return { status: 'success' };
    },
    [syncReferences]
  );

  return {
    draft: draftQuery.data ?? null,
    isLoading: draftQuery.isLoading,
    isSaving: isPending,
    syncReferences,
    awaitPendingSync,
    retryAutoSyncFailure,
    getUnavailableResourceKeys,
    reconcileUnavailableResourceKeys,
  };
}
